import type { UserAccount } from '@/types/auth'
import type { Ledger } from '@/types/ledger'
import type { Permission } from '@/types/role'

// 扩展 UserRole 以支持台账管理的三角色体系
export type PermissionRole = 'leader' | 'manager' | 'staff' | 'admin'

/**
 * 匹配存储的 ID 是否与当前用户 ID 对应
 */
function matchUserId(storedId: string, userId: string): boolean {
  return storedId === userId
}

function isOwnerOrMember(ledger: Ledger, userId: string): boolean {
  return matchUserId(ledger.ownerId, userId) || ledger.memberIds.some((id) => matchUserId(id, userId))
}

/**
 * 检查台账是否处于只读锁定状态
 * pending_review / approved / archived 状态下禁止修改
 */
function isLedgerReadonly(ledger: Ledger): boolean {
  return ledger.status === 'pending_review' || ledger.status === 'approved' || ledger.status === 'archived'
}

/**
 * 将 auth 模块的 UserRole 转换为台账权限角色
 * 注意：standard 角色映射为 staff
 */
export function toPermissionRole(role: string): PermissionRole {
  if (role === 'admin' || role === 'super_admin') return 'admin'
  if (role === 'leader' || role === 'manager') return role
  // standard 及未知角色一律按最小权限映射为 staff
  return 'staff'
}

/**
 * 检查用户是否可以创建台账
 */
export function canCreateLedger(user: UserAccount | null): boolean {
  if (!user) return false
  const role = toPermissionRole(user.role)
  return role === 'leader' || role === 'manager' || role === 'admin'
}

/**
 * 检查用户是否可以删除指定台账
 */
export function canDeleteLedger(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  if (isLedgerReadonly(ledger)) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  if (role === 'manager') return matchUserId(ledger.ownerId, user.id)
  return false
}

/**
 * 检查用户是否可以对台账执行智能分析
 * 仅超级管理员、单位领导、单位管理员可用
 * SYS_ADMIN（role='admin'）不拥有 workspace:analysis 权限，需隔离
 */
export function canAnalyzeLedger(user: UserAccount | null): boolean {
  if (!user) return false
  // SUPER_ADMIN has all permissions; SYS_ADMIN does NOT have workspace:analysis
  if (user.role === 'super_admin') return true
  const role = toPermissionRole(user.role)
  return role === 'leader' || role === 'manager'
}

/**
 * 检查用户是否可以编辑台账结构
 */
export function canEditStructure(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  if (isLedgerReadonly(ledger)) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  if (role === 'manager') return isOwnerOrMember(ledger, user.id)
  return false
}

/**
 * 检查用户是否可以编辑台账基本信息
 */
export function canEditLedger(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  if (isLedgerReadonly(ledger)) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  if (role === 'manager') return matchUserId(ledger.ownerId, user.id)
  return false
}

/**
 * 检查用户是否可以分配任务
 */
export function canAssignTask(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  if (isLedgerReadonly(ledger)) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  if (role === 'manager') return isOwnerOrMember(ledger, user.id)
  return false
}

/**
 * 检查用户是否可以上传材料到指定台账
 */
export function canUploadMaterial(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  if (isLedgerReadonly(ledger)) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  return isOwnerOrMember(ledger, user.id)
}

/**
 * 检查用户是否可以提交审核
 */
export function canSubmitReview(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  if (isLedgerReadonly(ledger)) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  return isOwnerOrMember(ledger, user.id)
}

/**
 * 检查用户是否可以查看台账详情
 */
export function canViewLedger(user: UserAccount | null, ledger: Ledger): boolean {
  if (!user) return false
  const role = toPermissionRole(user.role)
  if (role === 'leader' || role === 'admin') return true
  return isOwnerOrMember(ledger, user.id)
}

/**
 * 将权限树扁平化为 ID 列表
 */
export function flattenPermissionIds(tree: Permission[]): string[] {
  const ids: string[] = []
  function walk(nodes: Permission[]) {
    for (const node of nodes) {
      ids.push(node.id)
      if (node.children) walk(node.children)
    }
  }
  walk(tree)
  return ids
}

// ============ 新增：权限码常量（与后端 RolePresetConfig 对齐） ============

export const PERMISSION_CODES = {
  // 修复审查#11：原 LEDGER_* 四值为废弃的 perm_ws_ledger_*，后端实发码是 workspace:ledger:*
  // （见 RoleInitService 登记与 @PreAuthorize 消费），字面量不符使角色兜底判定永不命中；
  // 归档按钮后端消费的是 workspace:ledger:delete，故不再有独立的 archive 码
  LEDGER_VIEW: 'workspace:ledger:view',
  LEDGER_CREATE: 'workspace:ledger:create',
  LEDGER_EDIT: 'workspace:ledger:edit',
  // 修复台账审查#1：提审与审核拆权（后端 RoleInitService/RolePresetConfig 同名登记）
  LEDGER_SUBMIT: 'workspace:ledger:submit',
  LEDGER_DELETE: 'workspace:ledger:delete',
  LEDGER_AUDIT: 'workspace:ledger:audit',
  LEDGER_EXPORT: 'workspace:ledger:export',
  /** 工作任务（workspace 域） */
  TASK_VIEW: 'workspace:task:view',
  TASK_CREATE: 'workspace:task:create',
  TASK_UPDATE: 'workspace:task:update',
  TASK_DELETE: 'workspace:task:delete',
  /** 一键催办（修复任务审查#2：后端 urge-tasks 已由 hasAnyRole 改为按本码鉴权） */
  TASK_URGE: 'workspace:task:urge',
  /** 工作记录（workspace 域） */
  RECORD_VIEW: 'workspace:record:view',
  RECORD_CREATE: 'workspace:record:create',
  RECORD_EDIT: 'workspace:record:edit',
  RECORD_DELETE: 'workspace:record:delete',
  // 修复工作记录审查#8：审批/退回/批量审批/低质申诉处理四组端点由 hasAnyRole 角色串
  // 换为本细码鉴权（后端 RoleInitService 同名登记 perm_ws_record_approve，RolePresetConfig 只授 ORG_ADMIN）
  RECORD_APPROVE: 'workspace:record:approve',
  /** 自定义记录类型管理（组织管理员限本组织） */
  RECORD_TYPE_MANAGE: 'workspace:record:type:manage',
  /** 迎检日历（workspace 域，修复迎检审查#4：与后端 RoleInitService 登记同步） */
  INSPECTION_VIEW: 'workspace:inspection:view',
  INSPECTION_CREATE: 'workspace:inspection:create',
  INSPECTION_EDIT: 'workspace:inspection:edit',
  /** 终态退回（修复审查4c，spec 018 US-7 场景 5）：completed→in_progress 边的细码，仅 manager/leader */
  INSPECTION_REOPEN: 'workspace:inspection:reopen',
  INSPECTION_DELETE: 'workspace:inspection:delete',
  // 修复文档审查#13：原注释称三段式 doc:file:* / doc:folder:* 是「故意删除的死码」，该说法是错的——
  // 这些码在 RoleInitService 目录里真实存在（perm_doc_file_* / perm_doc_folder_*），且被后端
  // @PreAuthorize 逐端点消费；此前缺常量导致 views/document 等 29 处只能散写字面量，违反码表同源规范。
  // 下列常量值与后端 RoleInitService 登记的 code 逐字同源。
  DOC_FILE_UPLOAD: 'doc:file:upload',
  DOC_FILE_READ: 'doc:file:read',
  DOC_FILE_UPDATE: 'doc:file:update',
  DOC_FILE_DELETE: 'doc:file:delete',
  DOC_FILE_DOWNLOAD: 'doc:file:download',
  DOC_FILE_MOVE: 'doc:file:move',
  DOC_FILE_SHARE: 'doc:file:share',
  DOC_FOLDER_CREATE: 'doc:folder:create',
  DOC_FOLDER_READ: 'doc:folder:read',
  DOC_FOLDER_UPDATE: 'doc:folder:update',
  DOC_FOLDER_DELETE: 'doc:folder:delete',
  /** 水印配置（修复文档审查#3：后端四端点改为消费本码，前端门控随之从 doc:admin:cross-org 换来） */
  DOC_WATERMARK_CONFIG: 'doc:watermark:config',
  DOC_KB_SYNC: 'doc:knowledge:sync',
  DOC_KB_CHAT: 'doc:knowledge:chat',
  DOC_KB_MANAGE: 'doc:knowledge:manage',
  // 文档域页面/操作门控码（后端 RoleInitService 目录同名登记）
  DOC_CENTER: 'doc:center',
  DOC_STATS_VIEW: 'doc:stats:view',
  DOC_ADMIN_CROSS_ORG: 'doc:admin:cross-org',
  // 修复角色权限审查#3：角色权限管理域码补齐，逐一对应后端 RoleInitService 目录中真实存在的
  // admin:role:{view,create,edit,delete,permission,assign}；删除 ADMIN_ROLE_DATA_SCOPE——
  // 该码后端目录不存在（数据权限端点同样是 SUPER_ADMIN 硬门控），保留它只会让路由门控"碰巧"通过
  ADMIN_ROLE_VIEW: 'admin:role:view',
  ADMIN_ROLE_CREATE: 'admin:role:create',
  ADMIN_ROLE_EDIT: 'admin:role:edit',
  ADMIN_ROLE_DELETE: 'admin:role:delete',
  ADMIN_ROLE_PERMISSION: 'admin:role:permission',
  ADMIN_ROLE_ASSIGN: 'admin:role:assign',
  /** 统一审计日志页（原 admin:role:log / admin:config:audit / doc:audit:view 三枚分散码合并） */
  ADMIN_AUDIT_VIEW: 'admin:audit:view',
  ADMIN_OVERVIEW: 'admin:overview',
  ADMIN_ORG_VIEW: 'admin:org:view',
  // 修复单位审查#2/#3：单位管理写操作细码（后端 OrganizationAdminController 方法级 @PreAuthorize
  // 与 RoleInitService 目录同名登记，前端此前只登记 view/approve 两枚，写按钮零门控）
  ADMIN_ORG_CREATE: 'admin:org:create',
  ADMIN_ORG_EDIT: 'admin:org:edit',
  ADMIN_ORG_DELETE: 'admin:org:delete',
  ADMIN_PLAN_VIEW: 'admin:plan:view',
  ADMIN_LANDING_PAGE_MANAGE: 'admin:landing-page:manage',
  ADMIN_RECORD_TYPE_PRESET: 'admin:record-type:preset',
  ADMIN_USER_VIEW: 'admin:user:view',
  /** 系统配置（含归档规则），后端 RoleInitService 已登记，SYS_ADMIN 持有 */
  ADMIN_CONFIG_READ: 'admin:config:read',
  ADMIN_CONFIG_WRITE: 'admin:config:write',
  // 修复配置审查#12（口径②）：系统配置各子模块的「写」细码补齐登记。
  // 后端 RoleInitService 早已注册这六枚并逐端点 @PreAuthorize，前端码表此前零登记，
  // 于是自定义角色只持细码时：路由门控用 ADMIN_CONFIG_READ 放行页面，写按钮却无处比对，
  // 点下去整屏 403 且看不出是漏登记。读端点后端统一用 admin:config:read，故不再加细读码。
  // 注：admin:config:invoice 随发票配置整域下线，不登记。
  ADMIN_CONFIG_AI: 'admin:config:ai',
  ADMIN_CONFIG_PAYMENT: 'admin:config:payment',
  ADMIN_CONFIG_SECURITY: 'admin:config:security',
  ADMIN_CONFIG_NOTIFICATION: 'admin:config:notification',
  ADMIN_CONFIG_LEGAL: 'admin:config:legal',
  ADMIN_CONFIG_FILING: 'admin:config:filing',
  // 路由/菜单门控在用的其余管理端码（后端 RoleInitService 目录同名登记，此前只在 router/sidebar 里写散字面量）
  ADMIN_MESSAGE: 'admin:message',
  // 修复消息审查#8：SystemMessageAdminController 28 端点实按这两码鉴权（读 12 / 写 16），
  // 前端 5 视图 18 处写按钮此前散写 'admin:message:write' 字面量，现补登常量并全量改引。
  // 三枚断裂码 admin:message:announce/feedback/log 后端零消费、已从目录与预设整链删除，前端不再登记。
  ADMIN_MESSAGE_READ: 'admin:message:read',
  ADMIN_MESSAGE_WRITE: 'admin:message:write',
  ADMIN_ORG_APPROVE: 'admin:org:approve',
  /** 模板与组件预制管理页；后端 GET /api/templates 也按此码钳制「禁用模板」是否可见 */
  ADMIN_TEMPLATE_VIEW: 'admin:template:view',
  // 修复模板审查#3：后端 RoleInitService 早已登记 admin:template:manage 并授予 SYS_ADMIN，
  // 模板 4 个写端点与预制 3 个写端点全部按它 @PreAuthorize，而前端码表零登记 ——
  // 于是两页 9 个写按钮全无门控：只持 view 码的自定义角色看得见页面、点下去整屏 403。
  // 与 ADMIN_CONFIG_* 那批同因，补登记后按钮按实发码显隐。
  ADMIN_TEMPLATE_MANAGE: 'admin:template:manage',
  // 修复 P1#13：商业化（套餐/订单/订阅）细粒度权限码
  // 与后端 @PreAuthorize 的角色检查并存（前端按钮级，后端 API 兜底）
  // 修复计费审查#5：补登记视图在用但常量表缺失的两码
  // （后端 RoleInitService 已登记：admin:plan:create / admin:order:refund）
  // 修复计费审查#P1-3：删 admin:plan:price:edit——后端从无端点消费该码，价格档位随整套餐 PUT（admin:plan:edit）保存，
  // PlanDetailView 的增删价格按钮已改门控 ADMIN_PLAN_EDIT。
  ADMIN_PLAN_CREATE: 'admin:plan:create',
  ADMIN_PLAN_EDIT: 'admin:plan:edit',
  ADMIN_PLAN_DELETE: 'admin:plan:delete',
  ADMIN_PLAN_STATUS_EDIT: 'admin:plan:status:edit',
  ADMIN_PLAN_STATS_VIEW: 'admin:plan-stats:view',
  ADMIN_ORDER_VIEW: 'admin:order:view',
  ADMIN_ORDER_CANCEL: 'admin:order:cancel',
  ADMIN_ORDER_REFUND: 'admin:order:refund',
  /** 修复审查4f：银行转账（线下汇款）由管理员人工确认到账 */
  ADMIN_ORDER_PAY_CONFIRM: 'admin:order:pay-confirm',
  ADMIN_SUBSCRIPTION_VIEW: 'admin:subscription:view',
  ADMIN_SUBSCRIPTION_CHANGE: 'admin:subscription:change',
  /** 工作台首页（页面级码，路由与移动端 tab 共用） */
  WORKSPACE_DASHBOARD: 'workspace:dashboard',
  /** 智能分析（workspace 域） */
  WORKSPACE_ANALYSIS: 'workspace:analysis',
  WORKSPACE_ANALYSIS_VIEW: 'workspace:analysis:view',
  /** 宣传报道（workspace 域） */
  WORKSPACE_REPORT: 'workspace:report',
  WORKSPACE_REPORT_CREATE: 'workspace:report:create',
  WORKSPACE_REPORT_EDIT: 'workspace:report:edit',
  WORKSPACE_REPORT_DELETE: 'workspace:report:delete',
  WORKSPACE_REPORT_AUDIT: 'workspace:report:audit',
  WORKSPACE_REPORT_PUBLISH: 'workspace:report:publish',
} as const

// ============ 新增：基于权限码的权限判断 ============

/**
 * 检查是否为系统管理员或超级管理员
 */
export function isAdminRole(role: string | undefined | null): boolean {
  return role === 'admin' || role === 'super_admin'
}

/**
 * 检查是否仅为超级管理员（不含 SYS_ADMIN）
 * 用于需要区分 SUPER_ADMIN 和 SYS_ADMIN 的权限隔离场景
 */
export function isSuperAdmin(role: string | undefined | null): boolean {
  return role === 'super_admin'
}

/**
 * 检查是否为管理员（含系统管理员、超级管理员、单位管理员、单位领导）
 */
export function isManagerOrLeader(role: string | undefined | null): boolean {
  const r = toPermissionRole(role ?? '')
  return r === 'manager' || r === 'leader' || r === 'admin'
}

/**
 * 基于权限码的权限判断
 * 当前 fallback 到角色判断，后续后端返回 permissions 列表后优先使用
 *
 * 重要：仅 SUPER_ADMIN 跳过权限码检查；SYS_ADMIN（role='admin'）必须通过
 * 显式权限码校验，确保业务数据与系统管理权限隔离。
 */
export function hasPermissionCode(user: UserAccount | null, code: string): boolean {
  if (!user) return false
  // SUPER_ADMIN has all permissions dynamically; SYS_ADMIN must pass explicit checks
  if (user.role === 'super_admin') return true
  const userPermissions = user.permissions
  if (userPermissions?.includes(code)) return true
  return hasPermissionByRole(user.role, code)
}

export function hasPermissionByRole(role: string, code: string): boolean {
  const r = toPermissionRole(role)
  switch (code) {
    // 修复审查#11：台账/任务/记录三域的角色兜底，口径对齐后端 RolePresetConfig 的实发码
    // （manager=ORG_ADMIN、leader=ORG_LEADER、staff=STAFF；SYS_ADMIN 不持 workspace:* 业务码，
    // 与下方 workspace:analysis/report 的既有约定一致，此前 LEDGER_* 用废弃字面量导致这些分支永不命中）
    case PERMISSION_CODES.LEDGER_VIEW:
      return r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.LEDGER_EXPORT:
      return r === 'manager' || r === 'leader'
    case PERMISSION_CODES.LEDGER_CREATE:
      return r === 'manager'
    case PERMISSION_CODES.LEDGER_EDIT:
      // 修复台账审查#1：经办上传编辑材料（spec 003 FR-016），服务端限定 owner/成员/审核人
      return r === 'manager' || r === 'staff'
    case PERMISSION_CODES.LEDGER_SUBMIT:
      // 修复台账审查#1：负责人/成员提审（FR-015），服务端限定 owner/成员/审核人
      return r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.LEDGER_DELETE:
      return r === 'manager'
    case PERMISSION_CODES.LEDGER_AUDIT:
      // 修复台账审查#1：分管领导终审（FR-014）
      return r === 'manager' || r === 'leader'
    case PERMISSION_CODES.TASK_VIEW:
      return r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.TASK_CREATE:
      return r === 'manager' || r === 'leader'
    case PERMISSION_CODES.TASK_UPDATE:
      // STAFF 亦持 perm_ws_task_update（员工需自行流转任务状态），与 RolePresetConfig 同口径
      return r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.TASK_DELETE:
      return r === 'manager'
    case PERMISSION_CODES.TASK_URGE:
      // 修复任务审查#2：RolePresetConfig 只把催办码授给 ORG_ADMIN/ORG_LEADER，本兜底与其实发码同源。
      // 修复任务审查#R3：兜底范围窄不是因为「admin 点了必 403」——后端 resolveWorkspaceRole 把
      // SYS_ADMIN/SUPER_ADMIN 都归 LEADER，urgeAll 并不拒它们；这里只是不替管理岗凭空造出业务入口。
      return r === 'manager' || r === 'leader'
    case PERMISSION_CODES.RECORD_VIEW:
    case PERMISSION_CODES.RECORD_CREATE:
    case PERMISSION_CODES.RECORD_EDIT:
      return r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.RECORD_DELETE:
      return r === 'manager'
    case PERMISSION_CODES.RECORD_APPROVE:
      // 修复工作记录审查#8：后端五端点由 hasAnyRole('SUPER_ADMIN','ADMIN','MANAGER') 换成
      // hasAuthority('workspace:record:approve')，RolePresetConfig 只授 ORG_ADMIN——
      // leader 与 SYS_ADMIN 均不持该码（后者不持任何 workspace:*，SUPER_ADMIN 走 permissions 全量）
      return r === 'manager'
    case PERMISSION_CODES.RECORD_TYPE_MANAGE:
      return r === 'manager' || r === 'leader'
    // 迎检日历（修复迎检审查#4）：leader/admin/staff 持 view/create/edit，
    // delete 仅 manager，与后端 RolePresetConfig 逐格对齐
    case PERMISSION_CODES.INSPECTION_VIEW:
    case PERMISSION_CODES.INSPECTION_CREATE:
    case PERMISSION_CODES.INSPECTION_EDIT:
      return r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.INSPECTION_DELETE:
      return r === 'manager'
    // 修复审查4c：终态退回码授 manager/leader（RolePresetConfig 同口径），staff 与 SYS_ADMIN 无
    case PERMISSION_CODES.INSPECTION_REOPEN:
      return r === 'manager' || r === 'leader'
    // 文档域兜底映射（修复文档审查#13/#17/#20）：逐格对齐后端 RolePresetConfig 的 doc 段，
    // 只在实发权限数组缺失时兜底，不参与真实鉴权（后端 @PreAuthorize 才是裁决方）。
    case PERMISSION_CODES.DOC_FILE_UPLOAD:
      // 修复文档审查#17：STAFF 必须可上传——台账材料、工作记录附件、宣传报道配图三条链都经
      // FileAdder 统一路由到 POST /api/doc/files，少授这枚码经办一律 403。
      return r === 'admin' || r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.DOC_FILE_READ:
    case PERMISSION_CODES.DOC_FILE_DOWNLOAD:
    case PERMISSION_CODES.DOC_FOLDER_READ:
      return r === 'admin' || r === 'manager' || r === 'leader' || r === 'staff'
    case PERMISSION_CODES.DOC_FILE_UPDATE:
    case PERMISSION_CODES.DOC_FILE_DELETE:
    case PERMISSION_CODES.DOC_FILE_MOVE:
      return r === 'admin' || r === 'manager' || r === 'leader'
    case PERMISSION_CODES.DOC_FILE_SHARE:
    case PERMISSION_CODES.DOC_FOLDER_CREATE:
    case PERMISSION_CODES.DOC_FOLDER_UPDATE:
    case PERMISSION_CODES.DOC_FOLDER_DELETE:
      return r === 'admin' || r === 'manager'
    case PERMISSION_CODES.DOC_WATERMARK_CONFIG:
      // 修复文档审查#3：水印配置管的是跨组织数据（列表全量、可按任意 orgId 建配置），只归平台管理员
      return r === 'admin'
    case PERMISSION_CODES.DOC_STATS_VIEW:
      // 修复文档审查#20：统计报表收归管理角色，leader/staff 不再可见
      return r === 'admin' || r === 'manager'
    case PERMISSION_CODES.DOC_KB_SYNC:
      return r === 'admin' || r === 'manager'
    case PERMISSION_CODES.DOC_KB_CHAT:
      return r === 'admin' || r === 'leader' || r === 'manager' || r === 'staff'
    case PERMISSION_CODES.DOC_KB_MANAGE:
      return r === 'admin' || r === 'manager'
    // 修复 P1#13：商业化（套餐/订单/订阅）只允许 super_admin/admin
    case PERMISSION_CODES.ADMIN_PLAN_VIEW:
    case PERMISSION_CODES.ADMIN_PLAN_CREATE:
    case PERMISSION_CODES.ADMIN_PLAN_EDIT:
    case PERMISSION_CODES.ADMIN_PLAN_DELETE:
    case PERMISSION_CODES.ADMIN_PLAN_STATUS_EDIT:
    case PERMISSION_CODES.ADMIN_PLAN_STATS_VIEW:
    case PERMISSION_CODES.ADMIN_ORDER_VIEW:
    case PERMISSION_CODES.ADMIN_ORDER_CANCEL:
    case PERMISSION_CODES.ADMIN_ORDER_REFUND:
    case PERMISSION_CODES.ADMIN_SUBSCRIPTION_VIEW:
    case PERMISSION_CODES.ADMIN_SUBSCRIPTION_CHANGE:
      return r === 'admin'
    // workspace 业务权限：admin (SYS_ADMIN) 不拥有，仅 leader/manager
    case PERMISSION_CODES.WORKSPACE_ANALYSIS:
    case PERMISSION_CODES.WORKSPACE_ANALYSIS_VIEW:
    case PERMISSION_CODES.WORKSPACE_REPORT:
    case PERMISSION_CODES.WORKSPACE_REPORT_CREATE:
    case PERMISSION_CODES.WORKSPACE_REPORT_EDIT:
    case PERMISSION_CODES.WORKSPACE_REPORT_DELETE:
    case PERMISSION_CODES.WORKSPACE_REPORT_AUDIT:
    case PERMISSION_CODES.WORKSPACE_REPORT_PUBLISH:
      return r === 'leader' || r === 'manager'
    default:
      // SYS_ADMIN (r === 'admin') default: only admin:* and doc:* codes
      // workspace:* and unknown codes are denied for SYS_ADMIN
      if (r === 'admin') {
        return code.startsWith('admin:') || code.startsWith('doc:')
      }
      // 修复审查#11：未知码由 fail-open 改拒绝。原实现对 manager/leader 返回 true，
      // 权限表漏登记一个码就等于给单位管理员开了免检通道（按钮显示但后端 403，且看不出是没登记）。
      // 真实授权来源是后端下发的 permissions 列表；本函数只是列表缺失时的兜底，兜底不应放大权限。
      return false
  }
}

// ============ 新增：归档/激活静态权限判断 ============

/**
 * 检查用户是否有归档台账的权限
 * 注：实际能否归档还需调用后端 checkArchive 检查业务条件
 */
export function canArchiveLedger(user: UserAccount | null): boolean {
  if (!user) return false
  const r = toPermissionRole(user.role)
  return r === 'admin' || r === 'leader' || r === 'manager'
}

/**
 * 检查用户是否有激活已归档台账的权限
 */
export function canActivateLedger(user: UserAccount | null): boolean {
  if (!user) return false
  const r = toPermissionRole(user.role)
  return r === 'admin' || r === 'leader' || r === 'manager'
}
